/**
 * Where every card on the team board goes.
 *
 * The board used to be reflowing HTML, on the argument that a grouped tree is
 * shallow and its boxes are different heights. What that missed is width: a
 * real org grouped three levels deep, with sub-teams opened, is wider than any
 * window and a wrapping row has no way to zoom out of that. So it is the same
 * canvas the org chart uses, and this is its `layoutOrg`.
 *
 * It is the chart's layout, stacking and all: a card whose children are folded
 * is dealt into a column beside its siblings rather than given a place of its
 * own, so a department with eight teams costs two card widths instead of eight.
 *
 * The one thing it cannot borrow is a fixed card height. A team card carrying
 * eight people is four times the height of the department card beside it, so
 * `groupCardHeight` is the one answer — used to lay the card out *and* to draw
 * it, so the two can never disagree. Measuring the DOM instead would be a
 * second source of truth, and it reads as zero in jsdom.
 */

/** One width for every card. A grid of one size is the thing being read down. */
export const GROUP_CARD_WIDTH = 286;

/*
 * The card, measured rather than guessed. Every one of these was read off a
 * real browser, because the number here *is* the card's height — it is set on
 * the element — so a value that is too big is dead space at the bottom of every
 * box and one that is too small clips the last row.
 */
/** `pt-2.5` above the level label. */
const CARD_PAD_TOP = 10;
/** The small uppercase line naming the level: "Org", "Department", "Team". */
const LEVEL_LABEL_H = 14;
/** The name row — 27px of controls — and the `pb-2` under it. */
const TITLE_H = 35;
/** A member row, plus the 2px `space-y-0.5` between them. */
const MEMBER_ROW_H = 41;
/** Below the last row, since the card itself has no bottom padding. */
const CARD_PAD_BOTTOM = 10;
/**
 * Rows before the list scrolls inside the card. "Unassigned" holds three
 * hundred people in a 330-person org: a card a mile long is no use, and neither
 * is one that hides who is in it.
 */
export const MAX_MEMBER_ROWS = 8;
/** "Nobody on this team yet." */
const EMPTY_NOTE_H = 16;
/** Room under the last row for the fold pill, which straddles the bottom edge. */
const FOLD_ROOM = 12;

/** One layer down. */
const ROW_GAP = 56;
/**
 * Between stacked cards in a column — tighter than a row, because they read as
 * one list, but wide enough for the fold pill that straddles a card's edge.
 */
const STACK_GAP = 22;
/** Between the two columns of a stack; the trunk runs down the middle of it. */
const COLUMN_GAP = 30;
/** Between a parent's blocks: its stack, and each child that is opened. */
const BLOCK_GAP = 60;
/** Between whole trees, when the grouping has more than one root. */
const ROOT_GAP = 96;

/**
 * More children than this and they stack into columns instead of spreading.
 * Two side by side is narrower than two stacked, so stacking below that would
 * cost height for nothing.
 */
export const STACK_THRESHOLD = 2;
/** Columns a stack is dealt into, filled top to bottom. */
const STACK_COLUMNS = 2;

/** What the layout needs to know about a card. Everything else is the board's. */
export interface GroupLayoutNode {
  id: string;
  childIds: string[];
  /** People listed on this card itself. Only the deepest level has any. */
  memberCount: number;
}

export interface GroupPlacement {
  x: number;
  y: number;
  height: number;
}

/** A parent joined to a child. Drawn by the chart's own `TrunkEdge`. */
export interface GroupLayoutEdge {
  id: string;
  parentId: string;
  childId: string;
  /**
   * How the line is drawn. `spread` is the elbow from the bottom of the parent
   * into the top of a child that has a place of its own; `stack` is the list
   * form — a trunk dropped between two columns with a stub into each card.
   */
  kind: 'stack' | 'spread';
  /** Where the trunk runs. Only meaningful for a stacked edge. */
  trunkX: number;
  /** Which side of the card the stub enters. */
  side: 'left' | 'right';
}

export interface GroupLayoutResult {
  positions: Map<string, GroupPlacement>;
  /** Ids drawn this pass. */
  visibleIds: string[];
  /** Ids whose children exist but are folded away, and how many. */
  foldedChildCounts: Map<string, number>;
  edges: GroupLayoutEdge[];
  bounds: { minX: number; maxX: number; minY: number; maxY: number };
}

/**
 * How tall a card is, from what is on it.
 *
 * Deliberately arithmetic rather than measured. The card is given this height
 * explicitly, so the picture and the layout are the same number by
 * construction; a measured layout needs two passes and gets zero from jsdom,
 * which is where every component test lives.
 */
export function groupCardHeight(node: { memberCount: number; childIds: readonly string[] }): number {
  const body =
    node.memberCount === 0
      ? node.childIds.length === 0
        ? EMPTY_NOTE_H
        : 0
      : Math.min(node.memberCount, MAX_MEMBER_ROWS) * MEMBER_ROW_H;

  return (
    CARD_PAD_TOP +
    LEVEL_LABEL_H +
    TITLE_H +
    body +
    CARD_PAD_BOTTOM +
    (node.childIds.length > 0 ? FOLD_ROOM : 0)
  );
}

/** How tall the scrolling member list may be, for the card that draws it. */
export function memberListHeight(memberCount: number): number {
  return Math.min(memberCount, MAX_MEMBER_ROWS) * MEMBER_ROW_H;
}

/** A laid-out subtree, positioned from x = 0 and from the y it was given. */
interface Block {
  width: number;
  positions: Map<string, GroupPlacement>;
  edges: GroupLayoutEdge[];
}

/**
 * Lay the grouped tree out as a canvas of cards.
 *
 * The same shape as the org chart's `layoutOrg`, and for the same reason: a
 * card whose children are folded is dealt into a column beside its siblings
 * rather than given a place of its own, so a department with eight teams costs
 * two card widths instead of eight. Without that, every top-level value had to
 * be given a row of its own to keep the board on screen, and a board of
 * unrelated blocks stacked down the page is not a tree.
 *
 * `folded` names the cards whose children are hidden. It lists what is *shut*,
 * like the chart's `collapsedIds`; the default below is what decides the first
 * paint rather than the sense of the list.
 */
export function layoutGroups(
  nodes: ReadonlyMap<string, GroupLayoutNode>,
  rootIds: readonly string[],
  folded: ReadonlySet<string>,
): GroupLayoutResult {
  const positions = new Map<string, GroupPlacement>();
  const visibleIds: string[] = [];
  const foldedChildCounts = new Map<string, number>();
  const edges: GroupLayoutEdge[] = [];

  const heightOf = (id: string): number => {
    const node = nodes.get(id);
    return node ? groupCardHeight(node) : 0;
  };

  const childrenOf = (id: string): string[] => {
    const children = (nodes.get(id)?.childIds ?? []).filter((childId) => nodes.has(childId));
    if (children.length > 0 && folded.has(id)) {
      foldedChildCounts.set(id, children.length);
      return [];
    }
    return children;
  };

  /** A card is stackable when it is not showing cards of its own. */
  const isStackable = (id: string): boolean => childrenOf(id).length === 0;

  /**
   * The children that are showing nothing themselves, dealt into two columns
   * with a trunk down the middle.
   *
   * Rows are as tall as the tallest card in them rather than a fixed pitch,
   * which is the one thing this cannot borrow wholesale from the chart: a team
   * card carrying eight people is four times the height of the department card
   * beside it.
   */
  const stackBlock = (parentId: string, ids: string[], y: number): Block => {
    const rows = Math.ceil(ids.length / STACK_COLUMNS);
    const rowHeights: number[] = [];
    ids.forEach((id, index) => {
      const row = index % rows;
      rowHeights[row] = Math.max(rowHeights[row] ?? 0, heightOf(id));
    });

    const rowY: number[] = [];
    for (let row = 0; row < rows; row += 1) {
      rowY[row] = row === 0 ? y : (rowY[row - 1] ?? y) + (rowHeights[row - 1] ?? 0) + STACK_GAP;
    }

    const placed = new Map<string, GroupPlacement>();
    const stackEdges: GroupLayoutEdge[] = [];
    const trunkX = GROUP_CARD_WIDTH + COLUMN_GAP / 2;

    ids.forEach((id, index) => {
      const column = Math.floor(index / rows);
      const row = index % rows;
      placed.set(id, {
        x: column * (GROUP_CARD_WIDTH + COLUMN_GAP),
        y: rowY[row] ?? y,
        height: heightOf(id),
      });
      stackEdges.push({
        id: `g-${parentId}-${id}`,
        parentId,
        childId: id,
        kind: 'stack',
        trunkX,
        // The trunk runs between the columns, so the first is entered from its
        // right edge and the second from its left.
        side: column === 0 ? 'right' : 'left',
      });
    });

    const columns = Math.min(STACK_COLUMNS, ids.length);
    return {
      width: columns * GROUP_CARD_WIDTH + (columns - 1) * COLUMN_GAP,
      positions: placed,
      edges: stackEdges,
    };
  };

  const place = (id: string, y: number): Block => {
    const height = heightOf(id);
    const children = childrenOf(id);
    visibleIds.push(id);

    if (children.length === 0) {
      return {
        width: GROUP_CARD_WIDTH,
        positions: new Map([[id, { x: 0, y, height }]]),
        edges: [],
      };
    }

    const childY = y + height + ROW_GAP;
    const stacked = children.filter(isStackable);
    const spread = children.filter((childId) => !isStackable(childId));

    const blocks: Block[] = [];
    if (stacked.length > STACK_THRESHOLD) {
      for (const childId of stacked) visibleIds.push(childId);
      blocks.push(stackBlock(id, stacked, childY));
    } else {
      for (const childId of stacked) blocks.push(place(childId, childY));
    }
    for (const childId of spread) blocks.push(place(childId, childY));

    // Left to right, the stack first.
    const merged = new Map<string, GroupPlacement>();
    const mergedEdges: GroupLayoutEdge[] = [];
    let cursor = 0;
    for (const block of blocks) {
      for (const [key, placement] of block.positions) {
        merged.set(key, { ...placement, x: placement.x + cursor });
      }
      for (const edge of block.edges) mergedEdges.push({ ...edge, trunkX: edge.trunkX + cursor });
      cursor += block.width + BLOCK_GAP;
    }
    const childrenWidth = Math.max(0, cursor - BLOCK_GAP);

    /*
     * Centred over the cards of its direct children, not over the whole subtree.
     * A heading belongs above the row it names; centring over everything below
     * drags it away from that row as the grandchildren fan out.
     */
    const spans = children.map((childId) => merged.get(childId)?.x ?? 0);
    const left = Math.min(...spans);
    const right = Math.max(...spans) + GROUP_CARD_WIDTH;
    let selfX = (left + right) / 2 - GROUP_CARD_WIDTH / 2;

    // Nothing may sit at a negative offset: the block starts at zero.
    const shift = Math.max(0, -selfX);
    if (shift > 0) {
      for (const [key, placement] of merged) merged.set(key, { ...placement, x: placement.x + shift });
      for (const edge of mergedEdges) edge.trunkX += shift;
      selfX += shift;
    }
    merged.set(id, { x: selfX, y, height });

    // A child with a place of its own is joined from above, so its "trunk" is
    // its own centre — the same path builder, one corner shorter.
    for (const childId of children) {
      if (mergedEdges.some((edge) => edge.childId === childId)) continue;
      mergedEdges.push({
        id: `g-${id}-${childId}`,
        parentId: id,
        childId,
        kind: 'spread',
        trunkX: (merged.get(childId)?.x ?? 0) + GROUP_CARD_WIDTH / 2,
        side: 'left',
      });
    }

    return {
      width: Math.max(childrenWidth + shift, selfX + GROUP_CARD_WIDTH),
      positions: merged,
      edges: mergedEdges,
    };
  };

  /*
   * Every top-level value on one row, however many there are.
   *
   * They were shelved into rows for a while, because a dozen orgs side by side
   * is four thousand pixels of board. Stacking is the better answer to that —
   * it keeps each tree about two cards wide instead of fanning it out — and it
   * leaves the top row reading as what it is: one card per org, all at the same
   * height, the way the chart's first row reads.
   */
  let xOffset = 0;
  for (const rootId of rootIds) {
    if (!nodes.has(rootId)) continue;
    const block = place(rootId, 0);
    for (const [id, placement] of block.positions) {
      positions.set(id, { ...placement, x: placement.x + xOffset });
    }
    for (const edge of block.edges) edges.push({ ...edge, trunkX: edge.trunkX + xOffset });
    xOffset += block.width + ROOT_GAP;
  }

  return { positions, visibleIds, foldedChildCounts, edges, bounds: boundsOf(positions) };
}

function boundsOf(positions: Map<string, GroupPlacement>): GroupLayoutResult['bounds'] {
  if (positions.size === 0) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };

  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const { x, y, height } of positions.values()) {
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x + GROUP_CARD_WIDTH);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y + height);
  }
  return { minX, maxX, minY, maxY };
}

/** Every card with anything under it — so, everything "collapse all" folds. */
export function foldableIds(nodes: ReadonlyMap<string, { childIds: readonly string[] }>): Set<string> {
  const folded = new Set<string>();
  for (const [id, node] of nodes) {
    if (node.childIds.length > 0) folded.add(id);
  }
  return folded;
}

/**
 * What is folded when this browser has nothing to say about a grouping: all of
 * it, so the board opens as one row of top-level cards.
 *
 * The same first paint as the chart, and now right for the same reason — with
 * the children of a folded card dealt into two columns, the row you want is one
 * click away rather than a mile of board. It used to fold only a team that held
 * other teams, which was the whole of the nesting back when the levels above a
 * team were section headings; once they became cards that default opened the
 * entire company at once.
 *
 * Separate from `foldableIds` despite doing the same thing today, because they
 * answer different questions: one is a policy about a first look and the other
 * is what a button does.
 */
export function defaultFoldedGroups(
  nodes: ReadonlyMap<string, { childIds: readonly string[] }>,
): Set<string> {
  return foldableIds(nodes);
}
